export * from "./types.js";
export * from "./copy.js";
export { batteryRebate, paybackEstimate } from "./calculate.js";
export { rates_2026_01_01 } from "./rates-2026-01-01.js";
export { rates_2026_05_01 } from "./rates-2026-05-01.js";

import type { BatteryRebateSchedule } from "./types.js";
import { rates_2026_01_01 } from "./rates-2026-01-01.js";
import { rates_2026_05_01 } from "./rates-2026-05-01.js";

/**
 * Registry of Cheaper Home Batteries (SRES) rebate schedules, keyed by their
 * effective-from date (YYYY-MM-DD). The STC deeming factor steps down
 * twice-yearly (Jan + Jul), so this scheme is DATE-keyed, not FY-keyed — each
 * step registers a new schedule alongside the old one, and historical periods
 * stay registered so an install dated in an earlier window values against the
 * factor in force then. NEVER mutate a shipped schedule.
 *
 * RELOAD NOTE: re-verify CER every January and July; when the deeming factor
 * steps, close the live schedule's `effectiveTo` and register a new dated file.
 */
export const SOLAR_BATTERY_SCHEDULES: Record<string, BatteryRebateSchedule> = {
  "2026-01-01": rates_2026_01_01,
  "2026-05-01": rates_2026_05_01,
};

/** All registered schedules, sorted newest-first for date resolution. */
const SCHEDULES: readonly BatteryRebateSchedule[] = Object.values(
  SOLAR_BATTERY_SCHEDULES,
).sort((a, b) => (a.effectiveFrom < b.effectiveFrom ? 1 : -1));

/** Resolve a schedule by its registry key (effective-from date), or throw. */
export function batteryScheduleFor(scheduleKey: string): BatteryRebateSchedule {
  const rules = SOLAR_BATTERY_SCHEDULES[scheduleKey];
  if (!rules) {
    throw new Error(`no solar-battery schedule registered for ${scheduleKey}`);
  }
  return rules;
}

/**
 * Pick the battery-rebate schedule in force on an install date (YYYY-MM-DD).
 * Pure string comparison — ISO dates sort lexically. The current schedule is
 * open-ended (`effectiveTo: null`). Throws if the date predates every schedule
 * (e.g. a pre-1-May-2026 8.4-STC install before that file is registered).
 */
export function batteryScheduleForDate(installDate: string): BatteryRebateSchedule {
  for (const s of SCHEDULES) {
    const inWindow =
      installDate >= s.effectiveFrom &&
      (s.effectiveTo === null || installDate <= s.effectiveTo);
    if (inWindow) return s;
  }
  throw new Error(`no solar-battery schedule registered for install date ${installDate}`);
}

/** Most recent battery-rebate schedule — the default for live calcs. */
export const SOLAR_BATTERY_CURRENT_SCHEDULE = "2026-05-01";
